import React, { useState, useEffect, useMemo } from "react";
import {
    Pagination,
    Table,
    ScrollArea,
    Tabs,
    Group,
    TextInput,
    Select,
    Button,
    ActionIcon,
    Text,
    Box,
    Badge,
    Switch,
    Checkbox,
    Loader,
    Center,
    Modal,
    Textarea,
    LoadingOverlay,
    Grid,
    Tooltip,
    MultiSelect,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure, useDebouncedState, useDebouncedCallback } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
    IconSearch,
    IconRefresh,
    IconChevronDown,
    IconChevronRight,
    IconActivity,
    IconHandStop,
    IconPlus,
    IconTrash,
    IconEdit,
    IconKey,
    IconUsers,
    IconCheck,
    IconX,
    IconWindow,
    IconZoomReset,
    IconShieldLock,
    IconMenu2,
} from "@tabler/icons-react";
import GroupsRoleAdd from "../GroupsRoleAdd";
import BreadcrumbLayout1 from "../../../_base/component/_layout/_breadcrumb";
import {
    MantineReactTable,
    MRT_ColumnDef,
    useMantineReactTable,
    MRT_Row,
    MRT_SortingState,
    MRT_PaginationState,
    MRT_RowSelectionState,
} from "mantine-react-table";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { repositoryMaster, repositoryPos } from "../../../_base/_const/_constVar";
import { ListRoleModel } from "../../../model/ListRoleModel ";
import { MessageResponse } from "../../../model/MessageResponse";
import { UserMenuGroup } from "../../../model/UserMenuGroup";
import { ListAuthozireModel } from "../../../model/ListAuthozireModel";
import { SelectListItemBase } from "../../../_base/model/_base/SelectListItemBase";
import { getUserAll } from "../../../service/getSelectApi";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import { _localization } from "../../../config/location";
import { sky_blue } from "../../../const/variables";
import { _sideNavData } from "../../../_setup/navdata/_sideNavData";

// Types
export interface FunctionNode extends ListRoleModel {
    subRows?: FunctionNode[];
    _level?: number;
}

// Tree utilities
export const flattenTree = (
    nodes: FunctionNode[],
    expandedRowIds: Set<string>,
    level: number = 0
): FunctionNode[] => {
    return nodes.reduce<FunctionNode[]>((acc, node) => {
        const withLevel = { ...node, _level: level };
        acc.push(withLevel);

        if (node.subRows && node.subRows.length > 0 && expandedRowIds.has(node.id)) {
            acc.push(...flattenTree(node.subRows, expandedRowIds, level + 1));
        }

        return acc;
    }, []);
};

export const buildHierarchy = (
    data: FunctionNode[],
    parentId: string | null = null
): FunctionNode[] => {
    return data
        .filter((item) => (parentId === null ? !item.parentId : item.parentId === parentId))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "vi", { sensitivity: "base" }))
        .map((item) => ({
            ...item,
            subRows: buildHierarchy(data, item.id),
        }));
};

export const collectAllNodeIds = (nodes: FunctionNode[]): string[] => {
    const ids: string[] = [];
    const traverse = (node: FunctionNode) => {
        ids.push(node.id);
        if (node.subRows) {
            node.subRows.forEach(traverse);
        }
    };
    nodes.forEach(traverse);
    return ids;
};

export const getAssociatedKeys = (node: FunctionNode): string[] => {
    const keys: string[] = [];
    const traverse = (n: FunctionNode) => {
        const isLeaf = !n.subRows || n.subRows.length === 0;
        if (isLeaf) {
            const key = n.key || n.id;
            if (key) keys.push(key);
        } else if (n.subRows) {
            n.subRows.forEach(traverse);
        }
    };
    traverse(node);
    return keys;
};

const renderHierarchyLines = (level: number) => {
    if (level <= 0) return null;
    return Array.from({ length: level }).map((_, i) => (
        <Box
            key={i}
            style={{
                position: "absolute",
                left: i * 24 + 12,
                top: 0,
                bottom: 0,
                width: 1,
                borderLeft: "1px dashed var(--mantine-color-gray-3)",
            }}
        />
    ));
};

// Error Boundary
interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

class PermissionsErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error("PermissionsErrorBoundary caught an error:", error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: undefined });
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box p="xl" style={{ textAlign: "center" }}>
                    <Text size="lg" fw={700} c="red" mb="sm">
                        Đã có lỗi xảy ra khi tải phân quyền hệ thống
                    </Text>
                    <Text size="sm" c="dimmed" mb="md">
                        {this.state.error?.message || "Lỗi không xác định"}
                    </Text>
                    <Button onClick={this.handleRetry} color="blue">
                        Tải lại
                    </Button>
                </Box>
            );
        }

        return this.props.children;
    }
}

// Main Component inner
const PermissionsPageInner: React.FC = () => {
    const [activeTab, setActiveTab] = useState<string | null>("functions");

    useEffect(() => {
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            NotificationExtension.Fails(event.reason?.message || "Đã xảy ra lỗi hệ thống không xác định.");
        };

        window.addEventListener("unhandledrejection", handleUnhandledRejection);
        return () => window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    }, []);

    return (
        <Box p={2}>
            <BreadcrumbLayout1 />

            <Tabs value={activeTab} onChange={setActiveTab}>
                <Tabs.List mb={4}>
                    <Tabs.Tab value="functions" leftSection={<IconShieldLock size={16} />}>
                        Phân quyền chức năng
                    </Tabs.Tab>
                    <Tabs.Tab value="menus" leftSection={<IconMenu2 size={16} />}>
                        Phân quyền menu
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="functions">
                    <FunctionsTab />
                </Tabs.Panel>

                <Tabs.Panel value="menus">
                    <MenusTab />
                </Tabs.Panel>
            </Tabs>
        </Box>
    );
};

// Main Component
const PermissionsPage: React.FC = () => {
    return (
        <PermissionsErrorBoundary>
            <PermissionsPageInner />
        </PermissionsErrorBoundary>
    );
};

// Functions Tab
const FunctionsTab: React.FC = () => {
    const [rawData, setRawData] = useState<FunctionNode[]>([]);
    const [appOptions, setAppOptions] = useState<string[]>([]);
    const [filterApp, setFilterApp] = useState<string | null>(null);
    const [filterSelection, setFilterSelection] = useState<string>("all");
    const [searchQuery, setSearchQuery] = useDebouncedState("", 300);
    const [searchInput, setSearchInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);

    // User groups state
    const [groups, setGroups] = useState<ListAuthozireModel[]>([]);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [selectedGroup, setSelectedGroup] = useState<ListAuthozireModel | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
    const [sortBy, setSortBy] = useState<string>("az");
    const [userModalOpened, setUserModalOpened] = useState(false);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await repositoryMaster.get<MessageResponse<FunctionNode[]>>(
                "/api/v1/ListRole/keys-tree"
            );
            if (response && response.data) {
                const treeSorted = buildHierarchy(response.data);
                setRawData(treeSorted);

                const apps = Array.from(
                    new Set(
                        response.data
                            .map((item) => item.app)
                            .filter((app): app is string => !!app)
                    )
                );
                setAppOptions(apps);

                const rootIds = response.data
                    .filter((item) => !item.parentId)
                    .map((item) => item.id);
                setExpandedRowIds(new Set(rootIds));
            }
        } catch (error) {
            console.error("Error fetching permissions tree:", error);
            NotificationExtension.Fails("Không thể tải danh sách chức năng!");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            const res = await repositoryMaster.get<MessageResponse<ListAuthozireModel[]>>(
                "/api/v1/ListAuthozire/get-list"
            );
            if (res && res.success) {
                setGroups(res.data ?? []);
            }
        } catch (error) {
            console.error("Error fetching authorization groups:", error);
        }
    };

    const loadGroupPermissions = async (groupId: string) => {
        setIsLoading(true);
        try {
            const group = groups.find((g) => g.id.toString() === groupId) || null;
            setSelectedGroup(group);

            const res = await repositoryMaster.get<MessageResponse<string[]>>(
                `/api/v1/ListAuthozire/get-role?id=${groupId}`
            );
            if (res && res.success) {
                setSelectedRowIds(new Set(res.data || []));
            } else {
                setSelectedRowIds(new Set());
            }
        } catch (error) {
            console.error("Error loading group details:", error);
            NotificationExtension.Fails("Không thể tải cấu hình nhóm quyền!");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchGroups();
    }, []);

    useEffect(() => {
        if (selectedGroupId) {
            loadGroupPermissions(selectedGroupId);
        } else {
            setSelectedGroup(null);
            setSelectedRowIds(new Set());
        }
    }, [selectedGroupId]);

    const handleSync = async () => {
        setIsSyncing(true);
        try {
            const res = await repositoryMaster.get<MessageResponse<boolean>>(
                "/api/v1/RoleHandler/sync-keys-to-app"
            );
            if (res && res.success) {
                await fetchData();
                NotificationExtension.Success("Đồng bộ dữ liệu chức năng thành công!");
            } else {
                NotificationExtension.Fails("Đồng bộ dữ liệu thất bại!");
            }
        } catch (error) {
            console.error(error);
            NotificationExtension.Fails("Lỗi khi kết nối với máy chủ!");
        } finally {
            setIsSyncing(false);
        }
    };

    const hasCheckedDescendant = (node: FunctionNode): boolean => {
        const nodeKey = node.key || node.id;
        const isLeaf = !node.subRows || node.subRows.length === 0;
        if (isLeaf) {
            return !!nodeKey && selectedRowIds.has(nodeKey);
        }
        return !!node.subRows && node.subRows.some(hasCheckedDescendant);
    };

    const hasUncheckedDescendant = (node: FunctionNode): boolean => {
        const nodeKey = node.key || node.id;
        const isLeaf = !node.subRows || node.subRows.length === 0;
        if (isLeaf) {
            return !!nodeKey && !selectedRowIds.has(nodeKey);
        }
        return !!node.subRows && node.subRows.some(hasUncheckedDescendant);
    };

    const sortTreeNodes = (nodes: FunctionNode[]): FunctionNode[] => {
        return [...nodes].map(node => {
            if (node.subRows && node.subRows.length > 0) {
                return {
                    ...node,
                    subRows: sortTreeNodes(node.subRows)
                };
            }
            return node;
        }).sort((a, b) => {
            if (sortBy === "az") {
                return (a.name || "").localeCompare(b.name || "", "vi", { sensitivity: "base" });
            } else if (sortBy === "za") {
                return (b.name || "").localeCompare(a.name || "", "vi", { sensitivity: "base" });
            } else if (sortBy === "checkedFirst") {
                const hasA = !a.subRows || a.subRows.length === 0
                    ? !!(a.key || a.id) && selectedRowIds.has(a.key || a.id)
                    : hasCheckedDescendant(a);
                const hasB = !b.subRows || b.subRows.length === 0
                    ? !!(b.key || b.id) && selectedRowIds.has(b.key || b.id)
                    : hasCheckedDescendant(b);
                if (hasA && !hasB) return -1;
                if (!hasA && hasB) return 1;
                return (a.name || "").localeCompare(b.name || "", "vi", { sensitivity: "base" });
            } else if (sortBy === "uncheckedFirst") {
                const hasA = !a.subRows || a.subRows.length === 0
                    ? !!(a.key || a.id) && selectedRowIds.has(a.key || a.id)
                    : hasCheckedDescendant(a);
                const hasB = !b.subRows || b.subRows.length === 0
                    ? !!(b.key || b.id) && selectedRowIds.has(b.key || b.id)
                    : hasCheckedDescendant(b);
                if (!hasA && hasB) return -1;
                if (hasA && !hasB) return 1;
                return (a.name || "").localeCompare(b.name || "", "vi", { sensitivity: "base" });
            }
            return 0;
        });
    };

    const filterTreeBySearch = (nodes: FunctionNode[]): FunctionNode[] => {
        return nodes.reduce<FunctionNode[]>((acc, node) => {
            const filteredChildren = node.subRows ? filterTreeBySearch(node.subRows) : [];
            const appMatches = !filterApp || node.app === filterApp;
            const queryMatches =
                !searchQuery ||
                node.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                node.key?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                node.route?.toLowerCase().includes(searchQuery.toLowerCase());

            const isLeaf = !node.subRows || node.subRows.length === 0;
            const shouldKeep =
                (appMatches && queryMatches && isLeaf) || filteredChildren.length > 0;

            if (shouldKeep) {
                acc.push({
                    ...node,
                    subRows: filteredChildren,
                });
            }
            return acc;
        }, []);
    };

    const processedTree = useMemo(() => {
        const searchFiltered = filterTreeBySearch(rawData);
        const displayFiltered = (() => {
            if (filterSelection === "all") {
                return searchFiltered;
            }

            return searchFiltered.filter((node) => {
                if (filterSelection === "checked") {
                    return hasCheckedDescendant(node);
                } else if (filterSelection === "unchecked") {
                    return hasUncheckedDescendant(node);
                }
                return true;
            });
        })();
        return sortTreeNodes(displayFiltered);
    }, [rawData, filterApp, searchQuery, filterSelection, selectedRowIds, sortBy]);

    const getAllLeafNodes = (nodes: FunctionNode[]): FunctionNode[] => {
        const list: FunctionNode[] = [];
        const traverse = (node: FunctionNode) => {
            const isLeaf = !node.subRows || node.subRows.length === 0;
            if (isLeaf && node.parentId) {
                list.push(node);
            }
            if (node.subRows) {
                node.subRows.forEach(traverse);
            }
        };
        nodes.forEach(traverse);
        return list;
    };

    const checkableLeafNodes = useMemo(() => {
        return getAllLeafNodes(processedTree);
    }, [processedTree]);

    const totalLeavesCount = checkableLeafNodes.length;
    const checkedLeavesCount = useMemo(() => {
        return checkableLeafNodes.filter((node) => selectedRowIds.has(node.key || node.id || "")).length;
    }, [checkableLeafNodes, selectedRowIds]);

    const isAllSelected = useMemo(() => {
        if (checkableLeafNodes.length === 0) return false;
        return checkableLeafNodes.every((node) => selectedRowIds.has(node.key || node.id || ""));
    }, [checkableLeafNodes, selectedRowIds]);

    const isIndeterminate = useMemo(() => {
        if (checkableLeafNodes.length === 0) return false;
        const selectedCount = checkableLeafNodes.filter((node) =>
            selectedRowIds.has(node.key || node.id || "")
        ).length;
        return selectedCount > 0 && selectedCount < checkableLeafNodes.length;
    }, [checkableLeafNodes, selectedRowIds]);

    const handleToggleSelectAll = (checked: boolean) => {
        const next = new Set(selectedRowIds);
        if (checked) {
            checkableLeafNodes.forEach((node) => {
                const key = node.key || node.id;
                if (key) next.add(key);
            });
        } else {
            checkableLeafNodes.forEach((node) => {
                const key = node.key || node.id;
                if (key) next.delete(key);
            });
        }
        setSelectedRowIds(next);
    };

    const handleToggleRowExpand = (id: string) => {
        const next = new Set(expandedRowIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setExpandedRowIds(next);
    };

    const handleExpandAll = () => {
        const allIds = collectAllNodeIds(processedTree);
        setExpandedRowIds(new Set(allIds));
    };

    const handleCollapseAll = () => {
        setExpandedRowIds(new Set());
    };

    const handleSavePermissions = async () => {
        if (!selectedGroupId) return;
        setIsSaving(true);
        try {
            const keys = Array.from(selectedRowIds);
            const res = await repositoryMaster.post<MessageResponse<any>>(
                `/api/v1/ListAuthozire/auth-role?idAuth=${selectedGroupId}`,
                keys
            );
            if (res && res.success) {
                await loadGroupPermissions(selectedGroupId);
                NotificationExtension.Success("Lưu phân quyền chức năng thành công!");
            } else {
                NotificationExtension.Fails("Lưu phân quyền chức năng thất bại!");
            }
        } catch (error) {
            console.error("Error saving permissions:", error);
            NotificationExtension.Fails("Lỗi khi kết nối với máy chủ!");
        } finally {
            setIsSaving(false);
        }
    };

    // Recursive component to render checkbox tree
    const renderTreeNodes = (nodes: FunctionNode[], level: number = 0) => {
        const isAllLeaves = nodes.every((n) => !n.subRows || n.subRows.length === 0);

        return (
            <Box
                style={
                    isAllLeaves
                        ? {
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                            columnGap: "16px",
                            rowGap: "4px",
                            width: "100%",
                        }
                        : {
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px", // Increased space between subgroups/folders
                            width: "100%",
                        }
                }
            >
                {nodes.map((node) => {
                    const isLeaf = !node.subRows || node.subRows.length === 0;
                    const hasChildren = node.subRows && node.subRows.length > 0;
                    const isExpanded = expandedRowIds.has(node.id);
                    const nodeKey = node.key || node.id;

                    const associatedKeys = getAssociatedKeys(node);
                    const isChecked =
                        associatedKeys.length > 0 &&
                        associatedKeys.every((k) => selectedRowIds.has(k));
                    const isIndeterminate =
                        !isChecked && associatedKeys.some((k) => selectedRowIds.has(k));
                    const totalCount = associatedKeys.length;
                    const checkedCount = associatedKeys.filter((k) => selectedRowIds.has(k)).length;

                    return (
                        <Box
                            key={node.id}
                            style={{
                                gridColumn: isAllLeaves ? "auto" : "span 2",
                                width: "100%",
                                ...(level === 0 ? {
                                    backgroundColor: "var(--mantine-color-gray-0)",
                                    border: "1px solid var(--mantine-color-gray-2)",
                                    borderRadius: "8px",
                                    padding: "8px 12px 12px 12px",
                                    marginBottom: "12px",
                                } : {})
                            }}
                        >
                            <Group
                                gap="xs"
                                style={{
                                    minHeight: isLeaf ? 24 : 32,
                                    borderBottom: isAllLeaves
                                        ? "none"
                                        : "1px solid var(--mantine-color-gray-1)",
                                    display: "flex",
                                    alignItems: "center",
                                    marginBottom: hasChildren && isExpanded ? "8px" : "0",
                                }}
                            >
                                {hasChildren ? (
                                    <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleRowExpand(node.id);
                                        }}
                                    >
                                        {isExpanded ? (
                                            <IconChevronDown size={14} />
                                        ) : (
                                            <IconChevronRight size={14} />
                                        )}
                                    </ActionIcon>
                                ) : !isAllLeaves ? (
                                    <Box w={16} />
                                ) : null}

                                <Checkbox
                                    checked={isChecked}
                                    indeterminate={isIndeterminate}
                                    onChange={(e) => {
                                        const next = new Set(selectedRowIds);
                                        if (e.currentTarget.checked) {
                                            associatedKeys.forEach((k) => next.add(k));
                                        } else {
                                            associatedKeys.forEach((k) => next.delete(k));
                                        }
                                        setSelectedRowIds(next);
                                    }}
                                    size={hasChildren ? "sm" : "xs"}
                                    label={
                                        <Tooltip label={nodeKey} withArrow position="top" openDelay={200}>
                                            <Group gap={6} align="center" style={{ display: "inline-flex" }}>
                                                <Text
                                                    fw={hasChildren ? 600 : 400}
                                                    style={{
                                                        cursor: "pointer",
                                                        fontSize: hasChildren ? "12px" : "11px",
                                                    }}
                                                >
                                                    {node.name}
                                                </Text>
                                                {hasChildren && (
                                                    <Badge
                                                        size="xs"
                                                        variant="light"
                                                        color={checkedCount === totalCount ? "teal" : checkedCount > 0 ? "blue" : "gray"}
                                                        style={{ pointerEvents: "none" }}
                                                    >
                                                        {checkedCount}/{totalCount}
                                                    </Badge>
                                                )}
                                            </Group>
                                        </Tooltip>
                                    }
                                />
                            </Group>

                            {hasChildren && isExpanded && node.subRows && (
                                <Box
                                    style={{
                                        paddingLeft: "24px",
                                        marginLeft: "8px",
                                        borderLeft: "1px dashed var(--mantine-color-gray-3)",
                                        marginTop: "4px",
                                        marginBottom: "4px",
                                    }}
                                >
                                    {renderTreeNodes(node.subRows, level + 1)}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box p={0} style={{ minHeight: "auto" }}>
            <Group justify="space-between" mb={4} align="center">
                <Group align="center" gap="xs">
                    <Text fw={650} size="sm" style={{ whiteSpace: "nowrap" }}>Nhóm người dùng:</Text>
                    <Select
                        placeholder="Chọn nhóm..."
                        data={groups.map((g) => ({ value: g.id.toString(), label: g.name }))}
                        value={selectedGroupId}
                        onChange={setSelectedGroupId}
                        clearable
                        searchable
                        style={{ width: 220 }}
                    />
                    <ActionIcon
                        variant="light"
                        color="blue"
                        size="lg"
                        title="Thêm nhóm mới"
                        onClick={() => {
                            modals.open({
                                title: "Thêm mới nhóm quyền",
                                children: <GroupsRoleAdd onClose={fetchGroups} load={0} />,
                            });
                        }}
                    >
                        <IconPlus size={18} />
                    </ActionIcon>
                    {selectedGroupId && (
                        <Badge variant="outline" color="teal" size="sm">
                            Đã phân: {checkedLeavesCount}/{totalLeavesCount}
                        </Badge>
                    )}
                </Group>

                <Group gap="xs" align="center">
                    {selectedGroupId && (
                        <>
                            <Button
                                variant="filled"
                                color="teal"
                                onClick={handleSavePermissions}
                                loading={isSaving}
                            >
                                Lưu phân quyền
                            </Button>
                            <Button
                                variant="outline"
                                color="blue"
                                onClick={() => setUserModalOpened(true)}
                                leftSection={<IconUsers size={16} />}
                            >
                                Người dùng
                            </Button>
                        </>
                    )}
                    <Button
                        variant="filled"
                        leftSection={<IconRefresh size={16} />}
                        loading={isSyncing}
                        onClick={handleSync}
                    >
                        Đồng bộ chức năng
                    </Button>
                </Group>
            </Group>

            {selectedGroupId ? (
                <Box>
                    <Group justify="space-between" mb={6} p={4} style={{ backgroundColor: "var(--mantine-color-gray-0)", borderRadius: 6 }}>
                        <Group gap="xs" style={{ flex: 1 }}>
                            <TextInput
                                placeholder="Tìm kiếm chức năng..."
                                value={searchInput}
                                onChange={(e) => {
                                    setSearchInput(e.target.value);
                                    setSearchQuery(e.target.value);
                                }}
                                leftSection={<IconSearch size={16} />}
                                style={{ flex: 1, minWidth: 200 }}
                            />
                            <Select
                                placeholder="Tất cả apps"
                                data={appOptions.map((app) => ({ value: app, label: app }))}
                                value={filterApp}
                                onChange={setFilterApp}
                                clearable
                                style={{ width: 150 }}
                            />
                            <Select
                                value={filterSelection}
                                onChange={(val) => setFilterSelection(val || "all")}
                                data={[
                                    { value: "all", label: "Tất cả chức năng" },
                                    { value: "checked", label: "Đã phân quyền" },
                                    { value: "unchecked", label: "Chưa phân quyền" },
                                ]}
                                style={{ width: 170 }}
                            />
                            <Select
                                value={sortBy}
                                onChange={(val) => setSortBy(val || "az")}
                                data={[
                                    { value: "az", label: "Sắp xếp A-Z" },
                                    { value: "za", label: "Sắp xếp Z-A" },
                                    { value: "checkedFirst", label: "Đã tích lên đầu" },
                                    { value: "uncheckedFirst", label: "Chưa tích lên đầu" },
                                ]}
                                style={{ width: 180 }}
                            />
                        </Group>

                        <Group>
                            <Checkbox
                                label="Chọn tất cả hiển thị"
                                checked={isAllSelected}
                                indeterminate={isIndeterminate}
                                onChange={(e) => handleToggleSelectAll(e.currentTarget.checked)}
                            />
                            <Button variant="subtle" size="xs" onClick={handleExpandAll} disabled={isLoading}>
                                Mở rộng tất cả
                            </Button>
                            <Button variant="subtle" size="xs" onClick={handleCollapseAll} disabled={isLoading}>
                                Thu gọn tất cả
                            </Button>
                        </Group>
                    </Group>

                    {isLoading ? (
                        <Center py="xl">
                            <Loader size="lg" />
                        </Center>
                    ) : processedTree.length === 0 ? (
                        <Center py="xl" style={{ border: "1px dashed var(--mantine-color-gray-4)", borderRadius: 8 }}>
                            <Box style={{ textAlign: "center" }}>
                                <Text size="lg" c="dimmed" fw={500}>
                                    Không tìm thấy chức năng nào
                                </Text>
                                <Text size="sm" c="dimmed">
                                    Vui lòng thay đổi khóa tìm kiếm hoặc lọc ứng dụng.
                                </Text>
                            </Box>
                        </Center>
                    ) : (
                        <Box
                            p="xs"
                            style={{
                                border: "1px solid var(--mantine-color-gray-2)",
                                borderRadius: 8,
                                backgroundColor: "#fff",
                                maxHeight: "calc(100vh - 280px)",
                                overflowY: "auto",
                            }}
                        >
                            {renderTreeNodes(processedTree)}
                        </Box>
                    )}
                </Box>
            ) : (
                <Center py={80} style={{ border: "1px dashed var(--mantine-color-gray-3)", borderRadius: 8 }}>
                    <Box style={{ textAlign: "center" }}>
                        <Text size="lg" c="dimmed" fw={500}>
                            Nhóm quyền chưa được chọn
                        </Text>
                        <Text size="sm" c="dimmed">
                            Vui lòng chọn một nhóm người dùng ở trên để tải danh sách và phân quyền chức năng.
                        </Text>
                    </Box>
                </Center>
            )}

            {selectedGroupId && (
                <AssignUsersModal
                    opened={userModalOpened}
                    onClose={() => setUserModalOpened(false)}
                    idGroup={selectedGroupId}
                    groupName={groups.find((g) => g.id.toString() === selectedGroupId)?.name || ""}
                    type="function"
                />
            )}
        </Box>
    );
};

// Menus Tab (Menu Groups Checklist Tree)
const MenusTab: React.FC = () => {
    const [rawData, setRawData] = useState<FunctionNode[]>([]);
    const [appOptions, setAppOptions] = useState<string[]>([]);
    const [filterApp, setFilterApp] = useState<string | null>(null);
    const [filterSelection, setFilterSelection] = useState<string>("all");
    const [searchQuery, setSearchQuery] = useDebouncedState("", 300);
    const [searchInput, setSearchInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Menu groups state
    const [groups, setGroups] = useState<UserMenuGroup[]>([]);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [selectedGroup, setSelectedGroup] = useState<UserMenuGroup | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
    const [sortBy, setSortBy] = useState<string>("az");
    const [userModalOpened, setUserModalOpened] = useState(false);

    const [createModalOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const menuTree: FunctionNode[] = _sideNavData.map((group, groupIdx) => {
                const subRows: FunctionNode[] = (group.links || [])
                    .filter((link) => link.label !== "-1")
                    .map((link, idx) => ({
                        id: `menu-child-${groupIdx}-${idx}`,
                        parentId: `menu-group-${groupIdx}`,
                        name: link.label,
                        key: link.link,                 // This is the router path, which will be checked/unchecked
                        route: link.link,
                        app: group.label,
                    } as unknown as FunctionNode))
                    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "vi", { sensitivity: "base" }));

                return {
                    id: `menu-group-${groupIdx}`,
                    parentId: null,
                    name: group.label,
                    key: `menu-group-key-${groupIdx}`,
                    subRows,
                    app: group.label,
                } as unknown as FunctionNode;
            })
                .filter((group) => group.subRows && group.subRows.length > 0)
                .sort((a, b) => (a.name || "").localeCompare(b.name || "", "vi", { sensitivity: "base" }));

            setRawData(menuTree);

            const apps = Array.from(
                new Set(
                    menuTree
                        .map((item) => item.app)
                        .filter((app): app is string => !!app)
                )
            );
            setAppOptions(apps);

            const rootIds = menuTree
                .filter((item) => !item.parentId)
                .map((item) => item.id);
            setExpandedRowIds(new Set(rootIds));
        } catch (error) {
            console.error("Error building menu tree:", error);
            NotificationExtension.Fails("Không thể tải danh sách menu!");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            const res = await repositoryMaster.get<MessageResponse<UserMenuGroup[]>>(
                "/api/v1/menugroup/get-list?skip=0&take=1000"
            );
            if (res && res.success) {
                setGroups(res.data ?? []);
            }
        } catch (error) {
            console.error("Error fetching menu groups:", error);
        }
    };

    const loadGroupPermissions = async (groupId: string) => {
        setIsLoading(true);
        try {
            const res = await repositoryMaster.get<MessageResponse<UserMenuGroup>>(
                `/api/v1/menugroup/edit?id=${groupId}`
            );
            if (res && res.success && res.data) {
                setSelectedGroup(res.data);
                setSelectedRowIds(new Set(res.data.listrouter || []));
            } else {
                setSelectedGroup(null);
                setSelectedRowIds(new Set());
            }
        } catch (error) {
            console.error("Error loading group details:", error);
            NotificationExtension.Fails("Không thể tải cấu hình nhóm!");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchGroups();
    }, []);

    useEffect(() => {
        if (selectedGroupId) {
            loadGroupPermissions(selectedGroupId);
        } else {
            setSelectedGroup(null);
            setSelectedRowIds(new Set());
        }
    }, [selectedGroupId]);

    const hasCheckedDescendant = (node: FunctionNode): boolean => {
        const associated = getAssociatedKeys(node);
        return associated.some((key) => selectedRowIds.has(key));
    };

    const hasUncheckedDescendant = (node: FunctionNode): boolean => {
        const associated = getAssociatedKeys(node);
        return associated.some((key) => !selectedRowIds.has(key));
    };

    const sortTreeNodes = (nodes: FunctionNode[]): FunctionNode[] => {
        return [...nodes].map(node => {
            if (node.subRows && node.subRows.length > 0) {
                return {
                    ...node,
                    subRows: sortTreeNodes(node.subRows)
                };
            }
            return node;
        }).sort((a, b) => {
            if (sortBy === "az") {
                return (a.name || "").localeCompare(b.name || "", "vi", { sensitivity: "base" });
            } else if (sortBy === "za") {
                return (b.name || "").localeCompare(a.name || "", "vi", { sensitivity: "base" });
            } else if (sortBy === "checkedFirst") {
                const hasA = !a.subRows || a.subRows.length === 0
                    ? selectedRowIds.has(a.key || a.id)
                    : hasCheckedDescendant(a);
                const hasB = !b.subRows || b.subRows.length === 0
                    ? selectedRowIds.has(b.key || b.id)
                    : hasCheckedDescendant(b);
                if (hasA && !hasB) return -1;
                if (!hasA && hasB) return 1;
                return (a.name || "").localeCompare(b.name || "", "vi", { sensitivity: "base" });
            } else if (sortBy === "uncheckedFirst") {
                const hasA = !a.subRows || a.subRows.length === 0
                    ? selectedRowIds.has(a.key || a.id)
                    : hasCheckedDescendant(a);
                const hasB = !b.subRows || b.subRows.length === 0
                    ? selectedRowIds.has(b.key || b.id)
                    : hasCheckedDescendant(b);
                if (!hasA && hasB) return -1;
                if (hasA && !hasB) return 1;
                return (a.name || "").localeCompare(b.name || "", "vi", { sensitivity: "base" });
            }
            return 0;
        });
    };

    const filterTreeBySearch = (nodes: FunctionNode[]): FunctionNode[] => {
        return nodes.reduce<FunctionNode[]>((acc, node) => {
            const filteredChildren = node.subRows ? filterTreeBySearch(node.subRows) : [];
            const isLeaf = !node.subRows || node.subRows.length === 0;

            const matchesSearch =
                !searchQuery ||
                node.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                node.key?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                node.route?.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesApp = !filterApp || node.app === filterApp;

            let matchesSelection = true;
            if (filterSelection === "checked") {
                matchesSelection = isLeaf
                    ? selectedRowIds.has(node.key || node.id)
                    : hasCheckedDescendant(node);
            } else if (filterSelection === "unchecked") {
                matchesSelection = isLeaf
                    ? !selectedRowIds.has(node.key || node.id)
                    : hasUncheckedDescendant(node);
            }

            const shouldKeep =
                (matchesSearch && matchesApp && matchesSelection && isLeaf) ||
                filteredChildren.length > 0;

            if (shouldKeep) {
                acc.push({
                    ...node,
                    subRows: filteredChildren,
                });
            }
            return acc;
        }, []);
    };

    const processedTree = useMemo(() => {
        const filtered = filterTreeBySearch(rawData);
        return sortTreeNodes(filtered);
    }, [rawData, searchQuery, filterApp, filterSelection, selectedRowIds, sortBy]);

    const getAllLeafNodes = (nodes: FunctionNode[]): FunctionNode[] => {
        const list: FunctionNode[] = [];
        const traverse = (node: FunctionNode) => {
            const isLeaf = !node.subRows || node.subRows.length === 0;
            if (isLeaf) {
                list.push(node);
            }
            if (node.subRows) {
                node.subRows.forEach(traverse);
            }
        };
        nodes.forEach(traverse);
        return list;
    };

    const checkableLeafNodes = useMemo(() => {
        return getAllLeafNodes(processedTree);
    }, [processedTree]);

    const totalLeavesCount = checkableLeafNodes.length;
    const checkedLeavesCount = useMemo(() => {
        return checkableLeafNodes.filter((node) => selectedRowIds.has(node.key || node.id || "")).length;
    }, [checkableLeafNodes, selectedRowIds]);

    const isAllSelected = useMemo(() => {
        if (checkableLeafNodes.length === 0) return false;
        return checkableLeafNodes.every((node) => selectedRowIds.has(node.key || node.id));
    }, [checkableLeafNodes, selectedRowIds]);

    const isIndeterminate = useMemo(() => {
        if (checkableLeafNodes.length === 0) return false;
        const selectedCount = checkableLeafNodes.filter((node) =>
            selectedRowIds.has(node.key || node.id)
        ).length;
        return selectedCount > 0 && selectedCount < checkableLeafNodes.length;
    }, [checkableLeafNodes, selectedRowIds]);

    const handleToggleSelectAll = (checked: boolean) => {
        const next = new Set(selectedRowIds);
        if (checked) {
            checkableLeafNodes.forEach((node) => {
                const key = node.key || node.id;
                next.add(key);
            });
        } else {
            checkableLeafNodes.forEach((node) => {
                const key = node.key || node.id;
                next.delete(key);
            });
        }
        setSelectedRowIds(next);
    };

    const handleToggleRowExpand = (id: string) => {
        const next = new Set(expandedRowIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setExpandedRowIds(next);
    };

    const handleExpandAll = () => {
        const allIds = collectAllNodeIds(processedTree);
        setExpandedRowIds(new Set(allIds));
    };

    const handleCollapseAll = () => {
        setExpandedRowIds(new Set());
    };

    const handleSavePermissions = async () => {
        if (!selectedGroupId || !selectedGroup) return;
        setIsSaving(true);
        try {
            const updated = {
                ...selectedGroup,
                listrouter: Array.from(selectedRowIds),
            };
            const res = await repositoryMaster.post<MessageResponse<boolean>>(
                "/api/v1/menugroup/edit",
                updated
            );
            if (res && res.success) {
                await loadGroupPermissions(selectedGroupId);
                NotificationExtension.Success("Lưu phân quyền menu thành công!");
            } else {
                NotificationExtension.Fails("Lưu phân quyền menu thất bại!");
            }
        } catch (error) {
            console.error("Error saving menu permissions:", error);
            NotificationExtension.Fails("Có lỗi xảy ra khi lưu phân quyền!");
        } finally {
            setIsSaving(false);
        }
    };

    // Recursive component to render checkbox tree
    const renderTreeNodes = (nodes: FunctionNode[], level: number = 0) => {
        const isAllLeaves = nodes.every((n) => !n.subRows || n.subRows.length === 0);

        return (
            <Box
                style={
                    isAllLeaves
                        ? {
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                            columnGap: "16px",
                            rowGap: "4px",
                            width: "100%",
                        }
                        : {
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            width: "100%",
                        }
                }
            >
                {nodes.map((node) => {
                    const isLeaf = !node.subRows || node.subRows.length === 0;
                    const hasChildren = node.subRows && node.subRows.length > 0;
                    const isExpanded = expandedRowIds.has(node.id);
                    const nodeKey = node.key || node.id;

                    const associatedKeys = getAssociatedKeys(node);
                    const isChecked =
                        associatedKeys.length > 0 &&
                        associatedKeys.every((k) => selectedRowIds.has(k));
                    const isIndeterminate =
                        !isChecked && associatedKeys.some((k) => selectedRowIds.has(k));
                    const totalCount = associatedKeys.length;
                    const checkedCount = associatedKeys.filter((k) => selectedRowIds.has(k)).length;

                    return (
                        <Box
                            key={node.id}
                            style={{
                                gridColumn: isAllLeaves ? "auto" : "span 2",
                                width: "100%",
                                ...(level === 0 ? {
                                    backgroundColor: "var(--mantine-color-gray-0)",
                                    border: "1px solid var(--mantine-color-gray-2)",
                                    borderRadius: "8px",
                                    padding: "8px 12px 12px 12px",
                                    marginBottom: "12px",
                                } : {})
                            }}
                        >
                            <Group
                                gap="xs"
                                style={{
                                    minHeight: isLeaf ? 24 : 32,
                                    borderBottom: isAllLeaves
                                        ? "none"
                                        : "1px solid var(--mantine-color-gray-1)",
                                    display: "flex",
                                    alignItems: "center",
                                    marginBottom: hasChildren && isExpanded ? "8px" : "0",
                                }}
                            >
                                {hasChildren ? (
                                    <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleRowExpand(node.id);
                                        }}
                                    >
                                        {isExpanded ? (
                                            <IconChevronDown size={14} />
                                        ) : (
                                            <IconChevronRight size={14} />
                                        )}
                                    </ActionIcon>
                                ) : !isAllLeaves ? (
                                    <Box w={16} />
                                ) : null}

                                <Checkbox
                                    checked={isChecked}
                                    indeterminate={isIndeterminate}
                                    onChange={(e) => {
                                        const next = new Set(selectedRowIds);
                                        if (e.currentTarget.checked) {
                                            associatedKeys.forEach((k) => next.add(k));
                                        } else {
                                            associatedKeys.forEach((k) => next.delete(k));
                                        }
                                        setSelectedRowIds(next);
                                    }}
                                    size={hasChildren ? "sm" : "xs"}
                                    label={
                                        <Tooltip label={nodeKey} withArrow position="top" openDelay={200}>
                                            <Group gap={6} align="center" style={{ display: "inline-flex" }}>
                                                <Text
                                                    fw={hasChildren ? 600 : 400}
                                                    style={{
                                                        cursor: "pointer",
                                                        fontSize: hasChildren ? "12px" : "11px",
                                                    }}
                                                >
                                                    {node.name}
                                                </Text>
                                                {hasChildren && (
                                                    <Badge
                                                        size="xs"
                                                        variant="light"
                                                        color={checkedCount === totalCount ? "teal" : checkedCount > 0 ? "blue" : "gray"}
                                                        style={{ pointerEvents: "none" }}
                                                    >
                                                        {checkedCount}/{totalCount}
                                                    </Badge>
                                                )}
                                            </Group>
                                        </Tooltip>
                                    }
                                />
                            </Group>

                            {hasChildren && isExpanded && node.subRows && (
                                <Box
                                    style={{
                                        paddingLeft: "24px",
                                        marginLeft: "8px",
                                        borderLeft: "1px dashed var(--mantine-color-gray-3)",
                                        marginTop: "4px",
                                        marginBottom: "4px",
                                    }}
                                >
                                    {renderTreeNodes(node.subRows, level + 1)}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box p={0} style={{ minHeight: "auto" }}>
            <Group justify="space-between" mb={4} align="center">
                <Group align="center" gap="xs">
                    <Text fw={650} size="sm" style={{ whiteSpace: "nowrap" }}>Nhóm menu:</Text>
                    <Select
                        placeholder="Chọn nhóm..."
                        data={groups.map((g) => ({ value: g.id.toString(), label: g.name }))}
                        value={selectedGroupId}
                        onChange={setSelectedGroupId}
                        clearable
                        searchable
                        style={{ width: 220 }}
                    />
                    <ActionIcon
                        variant="light"
                        color="blue"
                        size="lg"
                        title="Thêm nhóm menu mới"
                        onClick={openCreate}
                    >
                        <IconPlus size={18} />
                    </ActionIcon>
                    {selectedGroupId && (
                        <Badge variant="outline" color="teal" size="sm">
                            Đã phân: {checkedLeavesCount}/{totalLeavesCount}
                        </Badge>
                    )}
                </Group>

                <Group gap="xs" align="center">
                    {selectedGroupId && (
                        <>
                            <Button
                                variant="filled"
                                color="teal"
                                onClick={handleSavePermissions}
                                loading={isSaving}
                            >
                                Lưu phân quyền
                            </Button>
                            <Button
                                variant="outline"
                                color="blue"
                                onClick={() => setUserModalOpened(true)}
                                leftSection={<IconUsers size={16} />}
                            >
                                Người dùng
                            </Button>
                        </>
                    )}
                </Group>
            </Group>

            {selectedGroupId ? (
                <Box>
                    <Group justify="space-between" mb={6} p={4} style={{ backgroundColor: "var(--mantine-color-gray-0)", borderRadius: 6 }}>
                        <Group gap="xs" style={{ flex: 1 }}>
                            <TextInput
                                placeholder="Tìm kiếm menu..."
                                value={searchInput}
                                onChange={(e) => {
                                    setSearchInput(e.target.value);
                                    setSearchQuery(e.target.value);
                                }}
                                leftSection={<IconSearch size={16} />}
                                style={{ flex: 1, minWidth: 200 }}
                            />
                            <Select
                                placeholder="Lọc theo Module"
                                data={appOptions.map((app) => ({ value: app, label: app }))}
                                value={filterApp}
                                onChange={setFilterApp}
                                clearable
                                style={{ width: 180 }}
                            />
                            <Select
                                value={filterSelection}
                                onChange={(val) => setFilterSelection(val || "all")}
                                data={[
                                    { value: "all", label: "Tất cả menu" },
                                    { value: "checked", label: "Đã phân quyền" },
                                    { value: "unchecked", label: "Chưa phân quyền" },
                                ]}
                                style={{ width: 170 }}
                            />
                            <Select
                                placeholder="Sắp xếp"
                                value={sortBy}
                                onChange={(val) => setSortBy(val || "az")}
                                data={[
                                    { value: "az", label: "Sắp xếp A-Z" },
                                    { value: "za", label: "Sắp xếp Z-A" },
                                    { value: "checkedFirst", label: "Đã tích lên đầu" },
                                    { value: "uncheckedFirst", label: "Chưa tích lên đầu" },
                                ]}
                                style={{ width: 180 }}
                            />
                        </Group>

                        <Group>
                            <Checkbox
                                label="Chọn tất cả hiển thị"
                                checked={isAllSelected}
                                indeterminate={isIndeterminate}
                                onChange={(e) => handleToggleSelectAll(e.currentTarget.checked)}
                            />
                            <Button variant="subtle" size="xs" onClick={handleExpandAll} disabled={isLoading}>
                                Mở rộng tất cả
                            </Button>
                            <Button variant="subtle" size="xs" onClick={handleCollapseAll} disabled={isLoading}>
                                Thu gọn tất cả
                            </Button>
                        </Group>
                    </Group>

                    {isLoading ? (
                        <Center py="xl">
                            <Loader size="lg" />
                        </Center>
                    ) : processedTree.length === 0 ? (
                        <Center py="xl" style={{ border: "1px dashed var(--mantine-color-gray-4)", borderRadius: 8 }}>
                            <Box style={{ textAlign: "center" }}>
                                <Text size="lg" c="dimmed" fw={500}>
                                    Không tìm thấy menu nào
                                </Text>
                                <Text size="sm" c="dimmed">
                                    Vui lòng thay đổi từ khóa tìm kiếm hoặc bộ lọc.
                                </Text>
                            </Box>
                        </Center>
                    ) : (
                        <Box
                            p="xs"
                            style={{
                                border: "1px solid var(--mantine-color-gray-2)",
                                borderRadius: 8,
                                backgroundColor: "#fff",
                                maxHeight: "calc(100vh - 280px)",
                                overflowY: "auto",
                            }}
                        >
                            {renderTreeNodes(processedTree)}
                        </Box>
                    )}
                </Box>
            ) : (
                <Center py={80} style={{ border: "1px dashed var(--mantine-color-gray-3)", borderRadius: 8 }}>
                    <Box style={{ textAlign: "center" }}>
                        <Text size="lg" c="dimmed" fw={500}>
                            Nhóm menu chưa được chọn
                        </Text>
                        <Text size="sm" c="dimmed">
                            Vui lòng chọn một nhóm menu ở trên để tải danh sách và phân quyền menu.
                        </Text>
                    </Box>
                </Center>
            )}

            <MenuGroupCreateModal
                opened={createModalOpen}
                onClose={closeCreate}
                onSuccess={fetchGroups}
            />

            {selectedGroupId && (
                <AssignUsersModal
                    opened={userModalOpened}
                    onClose={() => setUserModalOpened(false)}
                    idGroup={selectedGroupId}
                    groupName={groups.find((g) => g.id.toString() === selectedGroupId)?.name || ""}
                    type="menu"
                />
            )}
        </Box>
    );
};

// Menus Toolbar component
interface MenusTabToolbarProps {
    searchInput: string;
    setSearchInput: (val: string) => void;
    setQuery: (val: string) => void;
    selectedCount: number;
    onDelete: () => void;
    onActivate: () => void;
    onDeactivate: () => void;
    onAdd: () => void;
}

const MenusTabToolbar: React.FC<MenusTabToolbarProps> = ({
    searchInput,
    setSearchInput,
    setQuery,
    selectedCount,
    onDelete,
    onActivate,
    onDeactivate,
    onAdd,
}) => {
    return (
        <Group justify="space-between" mb="md">
            <Group>
                <TextInput
                    placeholder="Tìm nhóm menu..."
                    value={searchInput}
                    onChange={(e) => {
                        setSearchInput(e.target.value);
                        setQuery(e.target.value);
                    }}
                    style={{ width: 300 }}
                    leftSection={<IconSearch size={16} />}
                />
            </Group>

            <Group>
                {selectedCount > 0 && (
                    <>
                        <Text size="sm">Đã chọn {selectedCount}</Text>
                        <Button
                            variant="light"
                            color="green"
                            leftSection={<IconCheck size={16} />}
                            onClick={onActivate}
                        >
                            Kích hoạt
                        </Button>
                        <Button
                            variant="light"
                            color="orange"
                            leftSection={<IconX size={16} />}
                            onClick={onDeactivate}
                        >
                            Dừng
                        </Button>
                        <Button
                            variant="light"
                            color="red"
                            leftSection={<IconTrash size={16} />}
                            onClick={onDelete}
                        >
                            Xóa
                        </Button>
                    </>
                )}

                <Button variant="filled" leftSection={<IconPlus size={16} />} onClick={onAdd}>
                    Thêm mới
                </Button>
            </Group>
        </Group>
    );
};

// MenuGroupsTable component
interface MenuGroupsTableProps {
    data: UserMenuGroup[];
    isLoading: boolean;
    rowCount: number;
    pagination: MRT_PaginationState;
    onPaginationChange: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
    sorting: MRT_SortingState;
    onSortingChange: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
    rowSelection: MRT_RowSelectionState;
    onRowSelectionChange: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>>;
    onEdit: (group: UserMenuGroup) => void;
    onAssignRoles: (group: UserMenuGroup) => void;
    onAssignUsers: (group: UserMenuGroup) => void;
}

const MenuGroupsTable: React.FC<MenuGroupsTableProps> = ({
    data,
    isLoading,
    rowCount,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    rowSelection,
    onRowSelectionChange,
    onEdit,
    onAssignRoles,
    onAssignUsers,
}) => {
    const columns = useMemo<MRT_ColumnDef<UserMenuGroup>[]>(() => {
        return [
            {
                accessorKey: "name",
                header: "Tên nhóm menu",
                Cell: ({ cell, row }) => (
                    <Group>
                        <Text fw={500}>{cell.getValue<string>()}</Text>
                        {row.original.listrouter && row.original.listrouter.length > 0 && (
                            <Badge size="sm" variant="filled" color="blue">
                                {row.original.listrouter.length} chức năng
                            </Badge>
                        )}
                    </Group>
                ),
            },
            {
                id: "listrouter",
                header: "Chức năng mẫu",
                Cell: ({ row }) => {
                    const routers = row.original.listrouter || [];
                    return (
                        <Group wrap="wrap" gap={4}>
                            {routers.slice(0, 3).map((r, idx) => (
                                <Badge key={idx} size="xs" variant="light">
                                    {r}
                                </Badge>
                            ))}
                            {routers.length > 3 && (
                                <Badge size="xs" color="gray">
                                    +{routers.length - 3}
                                </Badge>
                            )}
                        </Group>
                    );
                },
            },
            {
                accessorKey: "active",
                header: "Trạng thái",
                Cell: ({ cell }) => (
                    <Badge color={cell.getValue<boolean>() ? "green" : "red"}>
                        {cell.getValue<boolean>() ? "Hoạt động" : "Dừng"}
                    </Badge>
                ),
            },
            {
                accessorKey: "createdate",
                header: "Ngày tạo",
                Cell: ({ cell }) => {
                    const date = cell.getValue<string>();
                    return date ? formatDateTransfer(date) : "-";
                },
            },
            {
                accessorKey: "createby",
                header: "Người tạo",
                Cell: ({ cell }) => cell.getValue<string>() || "-",
            },
            {
                id: "actions",
                header: "Thao tác",
                Cell: ({ row }) => (
                    <Group gap={4}>
                        <Tooltip label="Chỉnh sửa">
                            <ActionIcon
                                variant="subtle"
                                onClick={() => onEdit(row.original)}
                            >
                                <IconEdit size={16} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Phân quyền chức năng">
                            <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => onAssignRoles(row.original)}
                            >
                                <IconKey size={16} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Phân quyền nhân viên">
                            <ActionIcon
                                variant="subtle"
                                color="green"
                                onClick={() => onAssignUsers(row.original)}
                            >
                                <IconUsers size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                ),
            },
        ];
    }, [onEdit, onAssignRoles, onAssignUsers]);

    const table = useMantineReactTable({
        columns,
        data,
        manualPagination: true,
        manualSorting: true,
        rowCount,
        paginationDisplayMode: "pages",
        enableRowSelection: true,
        getRowId: (row) => row?.id?.toString() ?? "",
        onPaginationChange,
        onSortingChange,
        onRowSelectionChange,
        localization: _localization,
        state: {
            isLoading,
            pagination,
            sorting,
            rowSelection,
        },
    });

    return <MantineReactTable table={table} />;
};

// Create Modal
const MenuGroupCreateModal: React.FC<{
    opened: boolean;
    onClose: () => void;
    onSuccess: () => void;
}> = ({ opened, onClose, onSuccess }) => {
    const [submitting, setSubmitting] = useState(false);
    const form = useForm({
        initialValues: {
            name: "",
            active: true,
        },
        validate: {
            name: (val) => (val.trim().length === 0 ? "Tên nhóm menu là bắt buộc" : null),
        },
    });

    const loadDefaults = async () => {
        try {
            const res = await repositoryMaster.get<MessageResponse<UserMenuGroup>>(
                "/api/v1/menugroup/create"
            );
            if (res && res.data) {
                form.setValues({
                    name: res.data.name || "",
                    active: res.data.active ?? true,
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (opened) {
            form.reset();
            loadDefaults();
        }
    }, [opened]);

    const handleSubmit = async (values: typeof form.values) => {
        setSubmitting(true);
        try {
            const res = await repositoryMaster.post("/api/v1/menugroup/create", {
                id: 0,
                name: values.name,
                active: values.active,
            });
            if (res && res.success) {
                NotificationExtension.Success("Thêm mới nhóm menu thành công!");
                onSuccess();
                onClose();
            } else {
                NotificationExtension.Fails("Thêm mới thất bại!");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Tạo nhóm menu mới" size="md">
            <Box component="form" onSubmit={form.onSubmit(handleSubmit)} style={{ position: "relative" }}>
                <LoadingOverlay visible={submitting} />
                <Grid>
                    <Grid.Col span={12}>
                        <TextInput
                            label="Tên nhóm menu"
                            placeholder="Nhập tên..."
                            withAsterisk
                            {...form.getInputProps("name")}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <Checkbox
                            label="Trạng thái hoạt động"
                            checked={form.values.active}
                            onChange={(e) => form.setFieldValue("active", e.currentTarget.checked)}
                        />
                    </Grid.Col>
                </Grid>

                <Group justify="end" mt="md">
                    <Button variant="outline" color="gray" onClick={onClose} leftSection={<IconX size={16} />}>
                        Hủy
                    </Button>
                    <Button type="submit" color="blue" leftSection={<IconCheck size={16} />}>
                        Lưu
                    </Button>
                </Group>
            </Box>
        </Modal>
    );
};

// Edit Modal
const MenuGroupEditModal: React.FC<{
    opened: boolean;
    onClose: () => void;
    onSuccess: () => void;
    menuGroup: UserMenuGroup;
}> = ({ opened, onClose, onSuccess, menuGroup }) => {
    const [submitting, setSubmitting] = useState(false);
    const form = useForm({
        initialValues: {
            name: "",
            active: true,
        },
        validate: {
            name: (val) => (val.trim().length === 0 ? "Tên nhóm menu là bắt buộc" : null),
        },
    });

    const loadData = async () => {
        try {
            const res = await repositoryMaster.get<MessageResponse<UserMenuGroup>>(
                `/api/v1/menugroup/edit?id=${menuGroup.id}`
            );
            if (res && res.data) {
                form.setValues({
                    name: res.data.name || "",
                    active: res.data.active ?? true,
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (opened) {
            loadData();
        }
    }, [opened, menuGroup]);

    const handleSubmit = async (values: typeof form.values) => {
        setSubmitting(true);
        try {
            const res = await repositoryMaster.post("/api/v1/menugroup/edit", {
                ...menuGroup,
                name: values.name,
                active: values.active,
            });
            if (res && res.success) {
                NotificationExtension.Success("Chỉnh sửa nhóm menu thành công!");
                onSuccess();
                onClose();
            } else {
                NotificationExtension.Fails("Chỉnh sửa thất bại!");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Chỉnh sửa nhóm menu" size="md">
            <Box component="form" onSubmit={form.onSubmit(handleSubmit)} style={{ position: "relative" }}>
                <LoadingOverlay visible={submitting} />
                <Grid>
                    <Grid.Col span={12}>
                        <TextInput
                            label="Tên nhóm menu"
                            placeholder="Nhập tên..."
                            withAsterisk
                            {...form.getInputProps("name")}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <Checkbox
                            label="Trạng thái hoạt động"
                            checked={form.values.active}
                            onChange={(e) => form.setFieldValue("active", e.currentTarget.checked)}
                        />
                    </Grid.Col>
                </Grid>

                <Group justify="end" mt="md">
                    <Button variant="outline" color="gray" onClick={onClose} leftSection={<IconX size={16} />}>
                        Hủy
                    </Button>
                    <Button type="submit" color="blue" leftSection={<IconCheck size={16} />}>
                        Lưu
                    </Button>
                </Group>
            </Box>
        </Modal>
    );
};

// Assign Roles Modal
const AssignRolesModal: React.FC<{
    opened: boolean;
    onClose: () => void;
    idGroup: string;
    groupName: string;
    onSuccess: () => void;
}> = ({ opened, onClose, idGroup, groupName, onSuccess }) => {
    const [submitting, setSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [rawData, setRawData] = useState<FunctionNode[]>([]);
    const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [filterApp, setFilterApp] = useState<string | null>(null);
    const [appOptions, setAppOptions] = useState<string[]>([]);
    const [entity, setEntity] = useState<UserMenuGroup | null>(null);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const roleRes = await repositoryMaster.get<MessageResponse<FunctionNode[]>>(
                "/api/v1/ListRole/keys-tree"
            );
            const groupRes = await repositoryMaster.get<MessageResponse<UserMenuGroup>>(
                `/api/v1/menugroup/edit?id=${idGroup}`
            );

            if (roleRes && roleRes.data) {
                const structured = buildHierarchy(roleRes.data);
                setRawData(structured);

                const apps = Array.from(
                    new Set(roleRes.data.map((item) => item.app).filter((app): app is string => !!app))
                );
                setAppOptions(apps);

                // Initial expand
                const rootIds = roleRes.data.filter((item) => !item.parentId).map((item) => item.id);
                setExpandedRowIds(new Set(rootIds));
            }

            if (groupRes && groupRes.data) {
                setEntity(groupRes.data);
                setSelectedRowIds(new Set(groupRes.data.listrouter || []));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (opened) {
            loadData();
        }
    }, [opened, idGroup]);

    const filterTree = (nodes: FunctionNode[]): FunctionNode[] => {
        return nodes.reduce<FunctionNode[]>((acc, node) => {
            const filteredChildren = node.subRows ? filterTree(node.subRows) : [];
            const appMatches = !filterApp || node.app === filterApp;
            const queryMatches =
                !searchQuery ||
                node.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                node.key?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                node.route?.toLowerCase().includes(searchQuery.toLowerCase());

            const isLeaf = !node.subRows || node.subRows.length === 0;
            const shouldKeep =
                (appMatches && queryMatches && isLeaf) || filteredChildren.length > 0;

            if (shouldKeep) {
                acc.push({
                    ...node,
                    subRows: filteredChildren,
                });
            }
            return acc;
        }, []);
    };

    const processedTree = useMemo(() => {
        return filterTree(rawData);
    }, [rawData, filterApp, searchQuery]);

    const flatRows = useMemo(() => {
        return flattenTree(processedTree, expandedRowIds);
    }, [processedTree, expandedRowIds]);

    const checkableLeafNodes = useMemo(() => {
        const list: FunctionNode[] = [];
        const traverse = (node: FunctionNode) => {
            const isLeaf = !node.subRows || node.subRows.length === 0;
            if (isLeaf && node.parentId) {
                list.push(node);
            }
            if (node.subRows) {
                node.subRows.forEach(traverse);
            }
        };
        processedTree.forEach(traverse);
        return list;
    }, [processedTree]);

    const isAllSelected = useMemo(() => {
        if (checkableLeafNodes.length === 0) return false;
        return checkableLeafNodes.every((node) => selectedRowIds.has(node.key || node.id || ""));
    }, [checkableLeafNodes, selectedRowIds]);

    const isIndeterminate = useMemo(() => {
        if (checkableLeafNodes.length === 0) return false;
        const selectedCount = checkableLeafNodes.filter((node) =>
            selectedRowIds.has(node.key || node.id || "")
        ).length;
        return selectedCount > 0 && selectedCount < checkableLeafNodes.length;
    }, [checkableLeafNodes, selectedRowIds]);

    const handleToggleSelectAll = (checked: boolean) => {
        const next = new Set(selectedRowIds);
        if (checked) {
            checkableLeafNodes.forEach((node) => {
                const key = node.key || node.id;
                if (key) next.add(key);
            });
        } else {
            checkableLeafNodes.forEach((node) => {
                const key = node.key || node.id;
                if (key) next.delete(key);
            });
        }
        setSelectedRowIds(next);
    };

    const handleSave = async () => {
        if (!entity) return;
        setSubmitting(true);
        try {
            const updated = {
                ...entity,
                listrouter: Array.from(selectedRowIds),
            };
            const res = await repositoryMaster.post<MessageResponse<boolean>>(
                "/api/v1/menugroup/edit",
                updated
            );
            if (res && res.success) {
                NotificationExtension.Success("Phân quyền chức năng thành công!");
                onSuccess();
                onClose();
            } else {
                NotificationExtension.Fails("Phân quyền chức năng thất bại!");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    const columns = useMemo<MRT_ColumnDef<FunctionNode>[]>(() => {
        return [
            {
                id: "selection",
                header: "",
                Header: () => (
                    <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={(e) => handleToggleSelectAll(e.currentTarget.checked)}
                    />
                ),
                Cell: ({ row }) => {
                    const node = row.original;
                    const isLeaf = !node.subRows || node.subRows.length === 0;
                    const isCheckable = !!node.parentId && isLeaf;
                    const key = node.key || node.id;
                    const isSelected = selectedRowIds.has(key || "");

                    return (
                        <Checkbox
                            checked={isSelected}
                            disabled={!isCheckable}
                            onChange={(e) => {
                                const next = new Set(selectedRowIds);
                                if (e.currentTarget.checked) {
                                    if (key) next.add(key);
                                } else {
                                    if (key) next.delete(key);
                                }
                                setSelectedRowIds(next);
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    );
                },
                size: 50,
            },
            {
                accessorKey: "name",
                header: "Chức năng",
                Cell: ({ cell, row }) => {
                    const node = row.original;
                    const hasChildren = node.subRows && node.subRows.length > 0;
                    const isExpanded = expandedRowIds.has(node.id);
                    const levelPadding = (node._level || 0) * 24;

                    return (
                        <Group gap="xs" style={{ paddingLeft: levelPadding, position: "relative", minHeight: 32 }}>
                            {renderHierarchyLines(node._level || 0)}
                            {hasChildren ? (
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const next = new Set(expandedRowIds);
                                        if (next.has(node.id)) next.delete(node.id);
                                        else next.add(node.id);
                                        setExpandedRowIds(next);
                                    }}
                                >
                                    {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                                </ActionIcon>
                            ) : (
                                <Box w={22} style={{ display: "inline-block" }} />
                            )}
                            <Text size="sm" fw={hasChildren ? 600 : 400}>
                                {cell.getValue<string>()}
                            </Text>
                            {node.key && (
                                <Badge size="xs" variant="light" color="blue">
                                    {node.key}
                                </Badge>
                            )}
                        </Group>
                    );
                },
                size: 350,
            },
            {
                accessorKey: "app",
                header: "App",
                Cell: ({ cell }) => {
                    const val = cell.getValue<string>();
                    return val ? <Badge variant="outline">{val}</Badge> : "-";
                },
                size: 110,
            },
        ];
    }, [isAllSelected, isIndeterminate, selectedRowIds, expandedRowIds, processedTree]);

    const table = useMantineReactTable({
        columns,
        data: flatRows,
        enableExpanding: false,
        enablePagination: false,
        getRowId: (row) => row?.id || "",
        initialState: { density: "xs" },
        state: { isLoading },
    });

    return (
        <Modal opened={opened} onClose={onClose} title={`Phân quyền chức năng: ${groupName}`} size="xl">
            <Box style={{ position: "relative" }}>
                <LoadingOverlay visible={submitting} />

                <Group gap="md" justify="space-between" mb="md">
                    <Group style={{ flex: 1 }}>
                        <TextInput
                            placeholder="Tìm kiếm..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ flex: 1 }}
                            leftSection={<IconSearch size={16} />}
                        />
                        <Select
                            placeholder="Ứng dụng"
                            data={appOptions.map((app) => ({ value: app, label: app }))}
                            value={filterApp}
                            onChange={setFilterApp}
                            clearable
                            style={{ width: 180 }}
                        />
                    </Group>
                    <Group>
                        <Button
                            variant="subtle"
                            size="sm"
                            onClick={() => setExpandedRowIds(new Set(collectAllNodeIds(processedTree)))}
                        >
                            Mở tất cả
                        </Button>
                        <Button variant="subtle" size="sm" onClick={() => setExpandedRowIds(new Set())}>
                            Thu gọn
                        </Button>
                    </Group>
                </Group>

                <MantineReactTable table={table} />

                <Group justify="space-between" mt="md">
                    <Text size="sm" fw={500}>
                        Đã chọn: {selectedRowIds.size} chức năng
                    </Text>
                    <Group>
                        <Button variant="outline" color="gray" onClick={onClose} leftSection={<IconX size={16} />}>
                            Hủy
                        </Button>
                        <Button onClick={handleSave} color="blue" leftSection={<IconCheck size={16} />}>
                            Lưu thay đổi
                        </Button>
                    </Group>
                </Group>
            </Box>
        </Modal>
    );
};

// Assign Users Modal
const AssignUsersModal: React.FC<{
    opened: boolean;
    onClose: () => void;
    idGroup: string;
    groupName: string;
    type: "function" | "menu";
}> = ({ opened, onClose, idGroup, groupName, type }) => {
    const [submitting, setSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [data, setData] = useState<SelectListItemBase[]>([]);
    const [dataTemp, setDataTemp] = useState<SelectListItemBase[]>([]);
    const [keySearch, setKeySearch] = useState("");
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
    const [dataDepartmentAndBranch, setDataDepartmentAndBranch] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const pageSize = 25;

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const endpoint = type === "function"
                ? `/api/v1/ListAuthozire/get-emp?id=${idGroup}`
                : `/api/v1/menugroup/get-emp?id=${idGroup}`;
            const relationRes = await repositoryMaster.get<MessageResponse<string[]>>(endpoint);
            const idsEmp = relationRes?.data ?? [];

            const users = await getUserAll();
            if (users) {
                const sorted = users.sort((a, b) => {
                    const idxA = idsEmp.indexOf(a.value);
                    const idxB = idsEmp.indexOf(b.value);
                    if (idxA === -1 && idxB === -1) return 0;
                    if (idxA === -1) return 1;
                    if (idxB === -1) return -1;
                    return idxA - idxB;
                });

                setData(sorted);
                setDataTemp(sorted);

                const employeeIds = sorted.filter((u) => u.att3).map((u) => String(u.att3));
                if (employeeIds.length > 0) {
                    const empInfoRes = await repositoryPos.post<MessageResponse<any[]>>(
                        "/api/v1/TblDmEmployee/get-employee-info-by-ids",
                        { employeeIds }
                    );
                    if (empInfoRes && empInfoRes.success) {
                        setDataDepartmentAndBranch(empInfoRes.data ?? []);
                    }
                }

                const initialSelection: Record<string, boolean> = {};
                idsEmp.forEach((id) => {
                    initialSelection[id.toString()] = true;
                });
                setRowSelection(initialSelection);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (opened) {
            fetchData();
            setPage(1);
            setKeySearch("");
        }
    }, [opened, idGroup]);

    const handleSearch = useDebouncedCallback(async (query: string) => {
        if (query.length === 0) {
            setData(dataTemp);
        } else {
            const q = query.toLowerCase();
            const filtered = dataTemp.filter(
                (u) =>
                    u.text?.toLowerCase().includes(q) ||
                    u.att1?.toLowerCase().includes(q) ||
                    u.att2?.toString()?.toLowerCase().includes(q)
            );
            setData(filtered);
        }
        setKeySearch(query);
        setPage(1);
    }, 300);

    const handleSave = async () => {
        setSubmitting(true);
        try {
            const keys = Object.keys(rowSelection).filter((k) => rowSelection[k]);
            const endpoint = type === "function"
                ? "/api/v1/ListAuthozire/auth-user"
                : "/api/v1/menugroup/auth-menu";
            const res = await repositoryMaster.post<MessageResponse<boolean>>(
                endpoint,
                {
                    id: Number(idGroup),
                    keys,
                }
            );
            if (res && res.success) {
                NotificationExtension.Success("Gán nhân viên thành công!");
                onClose();
            } else {
                NotificationExtension.Fails("Gán nhân viên thất bại!");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    const getEmployeeInfo = (att3: any) => {
        if (!att3) return null;
        return dataDepartmentAndBranch.find(
            (i) => String(i.employeeId) === String(att3)
        );
    };

    const isAllSelected = data.length > 0 && data.every((u) => u.value && rowSelection[u.value.toString()]);
    const isSomeSelected = data.length > 0 && data.some((u) => u.value && rowSelection[u.value.toString()]) && !isAllSelected;

    const handleSelectAll = (checked: boolean) => {
        const newSelection = { ...rowSelection };
        data.forEach((u) => {
            if (u.value) {
                if (checked) {
                    newSelection[u.value.toString()] = true;
                } else {
                    delete newSelection[u.value.toString()];
                }
            }
        });
        setRowSelection(newSelection);
    };

    const handleSelectRow = (value: string, checked: boolean) => {
        const newSelection = { ...rowSelection };
        if (checked) {
            newSelection[value] = true;
        } else {
            delete newSelection[value];
        }
        setRowSelection(newSelection);
    };

    const selectedCount = Object.keys(rowSelection).filter((k) => rowSelection[k]).length;
    const startIndex = (page - 1) * pageSize;
    const paginatedData = data.slice(startIndex, startIndex + pageSize);

    return (
        <Modal opened={opened} onClose={onClose} title={`Phân quyền nhân viên: ${groupName}`} size="xl" centered>
            <Box style={{ position: "relative" }}>
                <LoadingOverlay visible={submitting || isLoading} />

                <Box style={{ display: "flex", flexDirection: "column", height: "70vh" }}>
                    <Box pb="md">
                        <TextInput
                            placeholder="Tìm kiếm nhân viên..."
                            defaultValue={keySearch}
                            onChange={(e) => handleSearch(e.currentTarget.value)}
                            leftSection={<IconSearch size={16} />}
                        />
                    </Box>

                    <ScrollArea style={{ flex: 1, minHeight: 0 }} offsetScrollbars>
                        <Table striped highlightOnHover verticalSpacing="xs">
                            <Table.Thead style={{ position: "sticky", top: 0, backgroundColor: "var(--mantine-color-body)", zIndex: 5 }}>
                                <Table.Tr>
                                    <Table.Th style={{ width: 40 }}>
                                        <Checkbox
                                            checked={isAllSelected}
                                            indeterminate={isSomeSelected}
                                            onChange={(e) => handleSelectAll(e.currentTarget.checked)}
                                        />
                                    </Table.Th>
                                    <Table.Th>Tên tài khoản</Table.Th>
                                    <Table.Th>Họ và tên</Table.Th>
                                    <Table.Th>Phòng ban</Table.Th>
                                    <Table.Th>Chi nhánh</Table.Th>
                                    <Table.Th>Điện thoại</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((user) => {
                                        const userId = user.value?.toString() || "";
                                        const isSelected = !!rowSelection[userId];
                                        const empInfo = getEmployeeInfo(user.att3);

                                        return (
                                            <Table.Tr key={userId}>
                                                <Table.Td>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(e) => handleSelectRow(userId, e.currentTarget.checked)}
                                                    />
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">{user.text}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">{user.att2 || "-"}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="xs">{empInfo?.departmentName || "-"}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="xs">{empInfo?.branchName || "-"}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="xs">{user.att1 || "-"}</Text>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })
                                ) : (
                                    <Table.Tr>
                                        <Table.Td colSpan={6} style={{ textAlign: "center", padding: "20px 0" }}>
                                            <Text c="dimmed" size="sm">
                                                Không tìm thấy nhân viên phù hợp
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>

                    <Box
                        style={{
                            borderTop: "1px solid var(--mantine-color-default-border)",
                            paddingTop: "var(--mantine-spacing-md)",
                            backgroundColor: "var(--mantine-color-body)",
                            zIndex: 10,
                            marginTop: "var(--mantine-spacing-md)",
                        }}
                    >
                        <Group justify="space-between" align="center">
                            <Group gap="lg">
                                <Text size="sm" fw={500}>
                                    Đã chọn: {selectedCount} nhân viên
                                </Text>
                                {data.length > pageSize && (
                                    <Pagination
                                        value={page}
                                        onChange={setPage}
                                        total={Math.ceil(data.length / pageSize)}
                                        size="sm"
                                    />
                                )}
                            </Group>
                            <Group>
                                <Button variant="outline" color="gray" onClick={onClose} leftSection={<IconX size={16} />}>
                                    Hủy
                                </Button>
                                <Button onClick={handleSave} color="blue" leftSection={<IconCheck size={16} />}>
                                    Lưu thay đổi
                                </Button>
                            </Group>
                        </Group>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default PermissionsPage;
